import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import api from 'utils/api';
import MainCard from 'components/MainCard';
import { brand } from 'utils/designTokens';

export const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'pickedup', label: 'PickedUp' },
  { value: 'outfordelivery', label: 'Out For Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'served', label: 'Served' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' }
];

export default function ReportFilters({ filters, onFilterChange, showStatusFilter = true }) {
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  // Fetch stores list for the store selection dropdown
  useEffect(() => {
    let active = true;
    const loadStores = async () => {
      setLoadingStores(true);
      try {
        const res = await api.get('/store/list');
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        if (active) {
          setStores(list);
        }
      } catch (err) {
        console.error('Failed to load stores inside filters:', err);
      } finally {
        if (active) {
          setLoadingStores(false);
        }
      }
    };
    loadStores();
    return () => {
      active = false;
    };
  }, []);

  const handleTextChange = (field) => (e) => {
    onFilterChange({ ...filters, [field]: e.target.value, page: 1 });
  };

  const handleDateChange = (field) => (val) => {
    const formatted = val ? dayjs(val).format('YYYY-MM-DD') : '';
    onFilterChange({ ...filters, [field]: formatted, page: 1 });
  };

  const handleStoreChange = (event, selectedStore) => {
    onFilterChange({ ...filters, storeId: selectedStore ? selectedStore.store_id : '', page: 1 });
  };

  const handleClearFilters = () => {
    onFilterChange({
      startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      storeId: '',
      search: '',
      status: '',
      page: 1
    });
  };

  const selectedStore = stores.find((s) => s.store_id === filters.storeId) || null;
  const mdSize = showStatusFilter ? 2 : 2.4;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MainCard sx={{ mb: 3.5, bgcolor: brand.gradientSoft }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range - Start Date */}
          <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
            <DatePicker
              label="Start Date"
              value={filters.startDate ? dayjs(filters.startDate) : null}
              onChange={handleDateChange('startDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  bgcolor: '#fff'
                }
              }}
            />
          </Grid>

          {/* Date Range - End Date */}
          <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
            <DatePicker
              label="End Date"
              value={filters.endDate ? dayjs(filters.endDate) : null}
              onChange={handleDateChange('endDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  bgcolor: '#fff'
                }
              }}
            />
          </Grid>

          {/* Store Dropdown */}
          <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
            <Autocomplete
              size="small"
              options={stores}
              getOptionLabel={(option) => option.title_en || 'Unknown Store'}
              value={selectedStore}
              onChange={handleStoreChange}
              loading={loadingStores}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Store"
                  placeholder={loadingStores ? 'Loading...' : 'Select store'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingStores ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                  sx={{ bgcolor: '#fff', borderRadius: 1 }}
                />
              )}
            />
          </Grid>

          {/* Search Bar */}
          <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Order no. or mobile"
              value={filters.search}
              onChange={handleTextChange('search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  </InputAdornment>
                )
              }}
              sx={{ bgcolor: '#fff', borderRadius: 1 }}
            />
          </Grid>

          {/* Status Dropdown */}
          {showStatusFilter && (
            <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filters.status}
                onChange={handleTextChange('status')}
                sx={{ bgcolor: '#fff', borderRadius: 1 }}
              >
                {ORDER_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Reset Button */}
          <Grid size={{ xs: 12, sm: 6, md: mdSize }}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={handleClearFilters}
              startIcon={<RestartAltRoundedIcon />}
              sx={{
                height: 38,
                borderRadius: 1,
                textTransform: 'none',
                bgcolor: '#fff',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </MainCard>
    </LocalizationProvider>
  );
}

ReportFilters.propTypes = {
  filters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    storeId: PropTypes.string,
    search: PropTypes.string,
    status: PropTypes.string,
    page: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  showStatusFilter: PropTypes.bool
};
